import { AlertTriangle, Shield } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  CoverageCategory,
  type CoverageListItem,
  CoverageStatus,
} from '@/services/types/coverage-types';

import { CoverageStatusBadge } from '../CoverageStatusBadge';
import { categoryConfig } from '../shared/constants';
import { useCoverageListLogic } from '../shared/hooks';

interface SelectableCoverageListProps {
  coverages: CoverageListItem[];
  selectedCoverageIds: string[];
  onSelectionChange: (coverageIds: string[]) => void;
  filterCategory?: CoverageCategory;
  onFilterChange: (category: CoverageCategory | undefined) => void;
}

export const SelectableCoverageList = ({
  coverages,
  selectedCoverageIds,
  onSelectionChange,
  filterCategory,
  onFilterChange,
}: SelectableCoverageListProps) => {
  // Use shared hooks for filtering and grouping logic
  const {
    filteredCoverages,
    rejectedCount,
    showRejected,
    setShowRejected,
    filteredGroups,
  } = useCoverageListLogic(coverages, filterCategory);

  const handleCoverageToggle = (coverageId: string) => {
    if (selectedCoverageIds.includes(coverageId)) {
      onSelectionChange(selectedCoverageIds.filter((id) => id !== coverageId));
    } else {
      onSelectionChange([...selectedCoverageIds, coverageId]);
    }
  };

  return (
    <div className="space-y-6">
      {/* Filter */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-medium text-gray-900">
            Select Coverages to Merge ({filteredCoverages.length})
          </h2>
          {rejectedCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowRejected(!showRejected)}
              className="text-gray-600 hover:text-gray-900"
            >
              {showRejected ? 'Hide' : 'Show'} Rejected ({rejectedCount})
            </Button>
          )}
        </div>
        <Select
          value={filterCategory || 'all'}
          onValueChange={(value) =>
            onFilterChange(
              value === 'all' ? undefined : (value as CoverageCategory),
            )
          }
        >
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {Object.entries(categoryConfig).map(([key, config]) => (
              <SelectItem key={key} value={key}>
                {config.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Coverage Groups */}
      {Object.entries(filteredGroups).map(([category, categoryCovers]) => {
        if (!categoryCovers || categoryCovers.length === 0) return null;

        const config = categoryConfig[category as CoverageCategory];
        const Icon = config?.icon || Shield;

        return (
          <div key={category} className="space-y-3">
            {/* Category Header */}
            <div className="flex items-center gap-2">
              <div
                className={cn(
                  'p-1.5 rounded-md',
                  config?.bgColor || 'bg-gray-100',
                )}
              >
                <Icon
                  className={cn('h-4 w-4', config?.color || 'text-gray-600')}
                />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">
                  {config?.label || 'Uncategorized'}
                </h3>
                <p className="text-xs text-gray-500">
                  {config?.description || 'Other coverage'}
                </p>
              </div>
            </div>

            {/* Coverage Cards */}
            <div className="space-y-2">
              {categoryCovers.map((coverage) => {
                const isSelected = selectedCoverageIds.includes(coverage.id);

                return (
                  <Card
                    key={coverage.id}
                    className={cn(
                      'p-5 transition-all cursor-pointer border-gray-100',
                      isSelected
                        ? 'border-blue-200 bg-blue-50/30 shadow-sm'
                        : 'hover:border-gray-200 hover:shadow-sm',
                    )}
                    onClick={() => handleCoverageToggle(coverage.id)}
                  >
                    <div className="flex items-center gap-4">
                      {/* Checkbox */}
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleCoverageToggle(coverage.id)}
                        className="flex-shrink-0"
                      />

                      {/* Coverage Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <h4 className="text-sm font-medium text-gray-900">
                              {coverage.category === CoverageCategory.PROPERTY
                                ? (coverage.named_insured?.[0] || coverage.carrier?.name || 'Unknown Property')
                                : (coverage.carrier?.name || 'Unknown Carrier')}
                            </h4>
                            <CoverageStatusBadge
                              status={
                                coverage.status || CoverageStatus.NEEDS_APPROVAL
                              }
                              showLabel={false}
                            />
                          </div>
                        </div>

                        {/* Coverage Details */}
                        <div className="space-y-1 text-sm text-gray-600">
                          {coverage.policy_type && (
                            <p>Type: {coverage.policy_type}</p>
                          )}
                          {coverage.policy_identifiers &&
                            coverage.policy_identifiers.length > 0 && (
                              <p>
                                Policy:{' '}
                                {
                                  coverage.policy_identifiers[0]
                                    .identifier_value
                                }
                              </p>
                            )}
                          {coverage.named_insured &&
                            coverage.named_insured.length > 0 && (
                              <p>Insured: {coverage.named_insured[0]}</p>
                            )}
                        </div>

                        {/* Warning for problematic selections */}
                        {coverage.status === CoverageStatus.REJECTED && (
                          <div className="flex items-center gap-1 mt-2 text-amber-600">
                            <AlertTriangle className="h-3 w-3" />
                            <span className="text-xs">Rejected coverage</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};
